/**
 * Diagnostic products
 * File Name: Products.tsx
 * Purpose: This component manages the display, addition, editing, and deletion of products.
 * It includes functionalities for listing products with pagination, searching, and filtering.
 * Date: 31-07-24
 */

import React, { useEffect, useState } from 'react';
import editIcon from '../../assets/images/edit-icon.png';
import deleteIcon from '../../assets/images/delete-icon.png';
import BreadCrumb from '../../Components/GlobalComponents/BreadCrumb';
import TableWrapper from '../../Components/GlobalComponents/TableWrapper';
import emtyImg from '../../assets/images/emty-img.png';
import UpsertProduct from '../../Components/AdminComponents/UpsertProduct';
import { IPaginated } from 'src/types/paginated';
import { IDiagnosticProductObj, IqueryParams } from 'src/types/GlobalInterfaces';
import SearchFilterInput from 'src/Components/GlobalComponents/SearchFilterInput';
import QueryString from "qs";
import httpRequest from 'src/helpers/httpRequest';
import { toast } from 'react-toastify';
import moment from 'moment';
import ConfirmationModal from 'src/Components/GlobalComponents/ConfirmationModal';
import ToggleRadioBtn from 'src/Components/ToggleRadioBtn';
import { useSelector } from 'react-redux';
import { RootState } from '@store/store';
import { canWrite } from "src/helpers/helperFunctions";
import { CURRENCY } from 'src/lib/constans';
interface IProductResponse {
  documents: IDiagnosticProductObj[];
  paginated: IPaginated;
}

const Products: React.FC = () => {
  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const hasWriteAccess = canWrite(userDetails, "diagnosticTest", false);
  const [isUpsert, setIsUpsert] = useState<boolean>(false); // State to manage the upsert mode (create/edit product)
  const [isEdit, setIsEdit] = useState<string | false>(false); // State to determine if the component is in edit mode

  const [statusLoader, setStatusLoader] = useState<string | null>(null);
  const [deleteLoader, setDeleteLoader] = useState<boolean>(false); // State to manage the loader during product deletion
  const [isLoader, setIsLoader] = useState<boolean>(false); // State to manage the loader for fetching products
  const [allProducts, setallProducts] = useState<IDiagnosticProductObj[]>([]); // State to store the list of products
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  }); // state for handle pagination like a current page, page limit & total pages

  const [deleteModal, setDeleteModal] = useState<boolean>(false); // State to control the visibility of the delete confirmation modal
  const [deleteId, setDeleteId] = useState<string>("");  // State to store the ID of the product to be deleted

  const [queryParams, setQueryParams] = useState<IqueryParams>({
    page: 1,
    limit: 10,
    search: "",
    bool: false
  });

  // Function change search text for api query params
  const handleChangeText = (txt: string) => {
    setQueryParams(prev => ({
      ...prev,
      page: 1,
      search: txt
    }));
  };

  // Function to update the list of categories after creating or editing a category
  const handleUpdateList = () => {
    setQueryParams(prev => ({
      ...prev,
      bool: !prev?.bool,
    }));
    setIsUpsert(false);
    setIsEdit(false);
  };

  // Function to handle page change in pagination
  const handlePageChange = (e: number) => {
    setQueryParams(prev => ({
      ...prev,
      page: e
    }));
  };

  // Function to set the component to edit mode and populate it with product data
  const handleEdit = (id: string) => {
    if (!hasWriteAccess) {
      return
    }
    setIsUpsert(true);
    setIsEdit(id);
  };

  // Function to show the delete confirmation modal and set the ID of the product to be deleted
  const handleDelete = (id: string) => {
    if (!hasWriteAccess) {
      return
    }
    setDeleteModal(true);
    setDeleteId(id);
  };

  // Function to close the delete confirmation modal
  const deleteModalClose = () => {
    setDeleteModal(false);
    setDeleteId("");
  };

  // API_CALL: Function to fetch all products based on query parameters
  const fetchAllProducts = async () => {
    setIsLoader(true);
    let { bool, ...remainingParams } = queryParams;
    const { res, err } = await httpRequest<IProductResponse>({ path: `/product/all?${QueryString.stringify(remainingParams)}` });
    if (res) {
      setallProducts(res?.documents);
      setPaginated(res?.paginated);
    } else {
      toast(err, { type: 'error' });
    }
    setIsLoader(false);
  };

  const handleStatusToogle = async (id: string) => {
    if (!hasWriteAccess) {
      return
    }
    setStatusLoader(id);
    const { res, err } = await httpRequest<{ message: string; status: boolean }>({
      method: "put",
      path: `/product/update-status/${id}`,
      params: {}
    });
    if (res) {
      const { message = "", status = false } = res;
      toast(message, { type: "success" });
      setallProducts(prev =>
        prev.map(v =>
          v?._id === id
            ? { ...v, status: status }
            : v
        ));
    } else {
      toast(err, { type: "error" });
    }
    setStatusLoader(null);
  };

  // API_CALL: Function to delete a product by its ID
  const deleteProducts = async () => {
    setDeleteLoader(true);
    const { res, err } = await httpRequest<any>({ method: "delete", path: `/product/delete/${deleteId}`, params: {} });
    if (res) {
      toast("Product deleted successfully.", { type: 'success' });
      setQueryParams(prev => ({
        ...prev,
        bool: !prev?.bool,
        page: allProducts?.length > 1 ? prev?.page : prev?.page > 1 ? prev?.page - 1 : prev?.page
      }));
      setDeleteModal(false);
      setDeleteId("");
    } else {
      toast(err, { type: 'error' });
    }
    setDeleteLoader(false);
  };

  useEffect(() => {
    fetchAllProducts();
  }, [queryParams]); // Re-fetch products when queryParams change

  return (
    <>
      <ConfirmationModal
        show={deleteModal}
        onClose={deleteModalClose}
        title="Are you sure you want to delete?"
        leftBtnText="No"
        rightBtnText="Yes"
        onConfirm={deleteProducts}
        loader={deleteLoader}
      />
      <BreadCrumb
        current="Products"
        previous="dashboard"
        navigateUrl="/admin"
        btnText={"Add Product"}
        btnShow={true}
        onPress={() => {
          if (!hasWriteAccess) {
            return
          }
          setIsUpsert(prev => {
            if (prev) {
              setIsEdit(false);
            }
            return !prev
          });
        }}
        isUpsert={isUpsert}
        btnClassName={`${!hasWriteAccess ? 'cursor-not-allowed' : ""}`}
        BtnId="add-product-btn"
      />
      {
        isUpsert
          ?
          <div className="table-container">
            <UpsertProduct
              onSuccess={handleUpdateList}
              isEdit={isEdit}
            />
          </div>
          :
          <div className="table-container">
            <SearchFilterInput
              onChangeText={handleChangeText}
              searchId="search-product-input"
            />
            <TableWrapper
              isLoading={isLoader}
              thead={["Image", "Product Name", "Category", "Status", "Price", "Date", "Action"]}
              page={paginated?.currentPage || 1}
              total={paginated?.totalPages || 0}
              columnWidth="12% 24% 20% 11% 11% 11% 11%"
              onChange={handlePageChange}
            >
              <div className='tbody-content-height'>
                {allProducts.map((item, index: number) => (
                  <ul key={index} className='font-size-16px tbody-light-table font-Poppins-Regular grid' style={{ gridTemplateColumns: "12% 24% 20% 11% 11% 11% 11%" }}>
                    <li className="action-last-colum-img" style={{ justifyContent: 'center', paddingLeft: 0, }}>
                      <img
                        src={`${item?.productImage}`}
                        alt="category"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement; // Type assertion
                          target.src = emtyImg; // Handle error by setting default image
                        }}
                      />
                    </li>
                    <li>{item?.productName || ""}</li>
                    <li className='coma-seprated-span'>
                      {item?.categoriesData?.map((v: any, i: number) => (
                        <span key={i}>{v?.name}</span>
                      ))}
                    </li>
                    <li className='justify-center' style={{ justifyContent: 'center', paddingLeft: 0, }}>
                      <div className={`${!hasWriteAccess ? 'read_access' : ""}`}>
                        <ToggleRadioBtn
                          isToggled={item?.status}
                          handleToggle={() => handleStatusToogle(item._id)}
                          loader={statusLoader === item?._id ? true : false}
                          id={`status-toggle-${index + 1}`}
                          disabled={!hasWriteAccess}
                        />
                      </div>
                    </li>
                    <li>{`${CURRENCY} ${item?.regularPrice || 0}`}</li>
                    <li>{moment(item?.createdAt).format("DD-MM-YYYY")}</li>
                    <li className='action-last-colum'>
                      <img id={`edit-product-${index + 1}`} onClick={() => handleEdit(item?._id)} src={editIcon} alt="" className={`${!hasWriteAccess ? 'read_access' : ""}`} />
                      <img id={`delete-product-${index + 1}`} onClick={() => handleDelete(item?._id)} src={deleteIcon} alt="" className={`${!hasWriteAccess ? 'read_access' : ""}`} />
                    </li>
                  </ul>
                ))}
              </div>
            </TableWrapper>
          </div>
      }
    </>
  );
};

export default Products;